import {
  makeStyles,
  tokens,
  mergeClasses,
} from '@coreai/fluentui-react';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { ottoTokens } from '@/theme/ottoTokens';
import { useNodeEditorFocusTrap } from './shared/useNodeEditorFocusTrap';

const useStyles = makeStyles({
  // Backdrop overlay - absolute to canvas container
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: ottoTokens.overlayBackdrop,
    backdropFilter: 'blur(4px)',
    zIndex: 1000,
    opacity: 0,
    visibility: 'hidden',
    transitionProperty: 'opacity, visibility',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveDecelerateMax,
  },
  backdropVisible: {
    opacity: 1,
    visibility: 'visible',
  },

  // Modal container - fills canvas with margin
  modal: {
    position: 'absolute',
    top: '24px',
    left: '24px',
    right: '24px',
    bottom: '24px',
    zIndex: 1001,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow64,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    opacity: 0,
    visibility: 'hidden',
    transform: 'scale(0.95)',
    transitionProperty: 'opacity, transform, visibility',
    transitionDuration: tokens.durationGentle,
    transitionTimingFunction: tokens.curveDecelerateMax,
  },
  modalVisible: {
    opacity: 1,
    visibility: 'visible',
    transform: 'scale(1)',
  },

  // Content area
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },
});

interface NodeEditorModalProps {
  children: React.ReactNode;
}

/**
 * NodeEditorModal - The modal container for the node editor
 * Handles backdrop, animations, and responsive sizing
 */
export const NodeEditorModal = ({ children }: NodeEditorModalProps) => {
  const styles = useStyles();
  const { containerRef: modalRef, handleKeyDown, handleBackdropClick } =
    useNodeEditorFocusTrap<HTMLDivElement>();

  // Use individual selectors to avoid infinite re-render loops
  const isOpen = useNodeEditorStore((s) => s.isOpen);

  return (
    <>
      {/* Backdrop */}
      <div
        className={mergeClasses(
          styles.backdrop,
          isOpen && styles.backdropVisible
        )}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={mergeClasses(
          styles.modal,
          isOpen && styles.modalVisible
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Node configuration"
        aria-hidden={!isOpen}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className={styles.content}>
          {children}
        </div>
      </div>
    </>
  );
};

NodeEditorModal.displayName = 'NodeEditorModal';
